import { useMemo } from "react";
import { scaleTime } from "d3-scale";
import type { SiteData } from "../types";
import { parseDateString } from "../lib/dates";
import { PIXELS_PER_MONTH, TIMELINE_PADDING_X } from "../components/Timeline/constants";

export interface TimelineScales {
  xScale: (d: Date) => number;
  totalWidth: number;
  startDate: Date;
  endDate: Date;
}

export function useTimelineScales(data: SiteData): TimelineScales {
  return useMemo(() => {
    const earliest = parseDateString(data.meta.earliestDate)!;
    const latestRaw = parseDateString(data.meta.latestDate)!;
    // Always pad the right edge to "now" + 6 months so ongoing roles have
    // visual room and the user can see a bit of future.
    const now = new Date();
    const latest =
      latestRaw.getTime() > now.getTime() ? latestRaw : new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1));

    const monthsSpan =
      (latest.getUTCFullYear() - earliest.getUTCFullYear()) * 12 +
      (latest.getUTCMonth() - earliest.getUTCMonth()) +
      1;
    const innerWidth = monthsSpan * PIXELS_PER_MONTH;
    const totalWidth = innerWidth + TIMELINE_PADDING_X * 2;

    const scale = scaleTime()
      .domain([earliest, latest])
      .range([TIMELINE_PADDING_X, TIMELINE_PADDING_X + innerWidth]);

    return {
      xScale: (d: Date) => scale(d),
      totalWidth,
      startDate: earliest,
      endDate: latest,
    };
  }, [data]);
}
